import { format, formatDistanceToNow } from 'date-fns'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FC, useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import ShareCVModal from '~/components/Share/ShareCVModal'
import configuration from '~/configuration'
import { PUBLIC_APP_URL } from '~/core/constants/env'
import { openAlert } from '~/core/ui/AlertDialog'
import { Divider } from '~/core/ui/Divider'
import { DropdownMenu } from '~/core/ui/DropdownMenu'
import IconWrapper from '~/core/ui/IconWrapper'
import { Input } from '~/core/ui/Input'
import { TextButton } from '~/core/ui/TextButton'
import { Tooltip } from '~/core/ui/Tooltip'
import { cn, debounce, uuidV4 } from '~/core/ui/utils'
import { downloadResume } from '~/lib/features/resumes/hooks/use-resume-download-builder'
import { previewResume } from '~/lib/features/resumes/hooks/use-resume-preview-builder'
import { IProfile } from '~/lib/features/resumes/types'
import useBoundStore from '~/lib/store'

const ResumeCard: FC<{
  data: IProfile
  onSubmitDuplicateProfile: (profileId: string) => void
  onSubmitDeleteProfile: (profileId: string, title: string) => void
  onSubmitUpdateProfile: (profileId: string, title: string | number) => void
}> = ({
  data,
  onSubmitDuplicateProfile,
  onSubmitDeleteProfile,
  onSubmitUpdateProfile
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { user } = useBoundStore()

  const [url, setUrl] = useState('')
  const [title, setTitle] = useState<string | number>(data.title)
  const [openErrorTitle, setErrorTitle] = useState(false)
  const isDestructive = String(title)?.length > 100
  const [openShareCVModal, setOpenShareCVModal] = useState<boolean>(false)
  useEffect(() => {
    previewResume({ shareLink: data.shareLink, id: data.id })
      .then((response) => {
        setUrl(response?.url)
      })
      .catch(() => {
        setUrl('error')
      })
  }, [])

  const debouncedChange = useCallback(
    debounce(async (value: string | number) => {
      onSubmitUpdateProfile(data.id, value)
      return Promise.resolve(true)
    }, 500),
    []
  )

  const [isLoading, setLoading] = useState(false)
  const handleGeneratePdf = useCallback(async () => {
    if (isLoading) return

    setLoading(true)
    downloadResume({ shareLink: data.shareLink, id: data.id })
      .then((response) => {
        const dataResponse = Uint8Array.from(response?.pdf?.data)
        const content = new Blob([dataResponse.buffer], {
          type: 'application/pdf'
        })

        const encodedUri = window.URL.createObjectURL(content)
        const link = document.createElement('a')

        link.setAttribute('href', encodedUri)
        link.setAttribute(
          'download',
          `CV-${(user.fullName || '').replace(
            /[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi,
            ''
          )}-${uuidV4()}`
        )

        link.click()
      })
      .catch(() => {
        // block code
      })
      .finally(() => {
        setLoading(false)
      })
  }, [data, user, isLoading])

  const renderInput = () => {
    return (
      <div className="group relative mr-2 flex flex-1 items-center text-sm font-medium">
        <Input
          destructive={isDestructive}
          placeholder="Untitled Resume"
          variant="ghost"
          onChange={(newValue) => {
            setErrorTitle(String(newValue)?.length >= 100)
            setTitle(newValue)
            if (String(newValue)?.length <= 100) {
              debouncedChange(newValue)
            }
          }}
          onBlur={(event) => {
            if (isDestructive) {
              setErrorTitle(false)
              setTitle(data.title)
            } else {
              if (event.target.value === '' || !event.target.value) {
                setTitle('Untitled Resume')
                onSubmitUpdateProfile(data.id, 'Untitled Resume')
              }
            }
          }}
          size="sm"
          className={cn(
            'peer -mx-px text-ellipsis pl-2 pr-2 hover:bg-transparent hover:pr-7 hover:text-primary-400 hover:placeholder:text-primary-400 focus:!pr-2',
            isDestructive === false
              ? 'focus:!text-gray-900 focus:placeholder:!text-gray-500'
              : 'focus:shadow-error'
          )}
          value={title}
        />
        <div className="absolute right-0 top-2/4 -z-[1] -mt-2 ml-1 hidden min-h-[16px] min-w-[16px] cursor-pointer group-hover:block peer-focus:hidden">
          <IconWrapper
            name="Edit3"
            size={16}
            className="text-gray-400 group-hover:text-primary-400"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-100">
      <div className="flex w-full items-center justify-between px-3 pt-3">
        <Tooltip
          open={openErrorTitle}
          align="end"
          content={isDestructive ? `Maximum 100 characters.` : undefined}
          classNameAsChild="w-full">
          {renderInput()}
        </Tooltip>
        {/* <div className="flex-none	py-2">
          <Badge color="orange" size="sm">
            75% profile strength
          </Badge>
        </div> */}
      </div>
      <p className="mb-4 mt-0.5 flex items-center px-5 text-xs text-gray-500">
        <div>
          Updated{' '}
          {formatDistanceToNow(new Date(data?.updatedAt), {
            includeSeconds: true
          })}
        </div>
        <div className="m-2 h-0.5 min-h-[2px] w-0.5 min-w-[2px] rounded bg-gray-400" />
        <div>
          Created on {format(new Date(data?.createdAt), 'MMM dd, yyyy')}
        </div>
      </p>
      <Divider />
      <div className="bg-gray-50 pb-6 pt-5">
        <div className="flex h-[386px] items-center justify-center">
          {url && url !== 'error' ? (
            <Link href={configuration.path.resumes.detail(data.id)}>
              <img src={url} width={274} height={386} alt="template name" />
            </Link>
          ) : url === '' ? (
            <svg
              aria-hidden="true"
              className="h-8 w-8 animate-spin fill-gray-400 text-transparent"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          ) : null}
        </div>
        <div className="mt-5 flex justify-center space-x-4 divide-x divide-gray-300">
          <TextButton
            iconMenus="Edit3"
            label="Edit"
            underline={false}
            onClick={() =>
              router.push(configuration.path.resumes.detail(data.id))
            }
          />
          <TextButton
            iconMenus={isLoading ? 'Loader2' : 'Download'}
            iconMenusLoading={isLoading ? 'animate-spin' : ''}
            label="Download PDF"
            underline={false}
            onClick={handleGeneratePdf}
            className="pl-4"
          />
          <DropdownMenu
            side="bottom"
            align="end"
            menuClassName="w-auto"
            menu={[
              {
                label: 'Share',
                icon: 'Share2',
                onClick: () => {
                  setOpenShareCVModal(true)
                }
              },
              {
                label: 'Duplicate',
                icon: 'Files',
                onClick: () => {
                  onSubmitDuplicateProfile(data.id)
                }
              },
              {
                label: 'Delete',
                icon: 'Trash2',
                color: 'red',
                onClick: () => {
                  openAlert({
                    className: 'w-[480px]',
                    title: t('dashboard:delete_resume:title') || '',
                    description: (
                      <Trans
                        i18nKey="dashboard:delete_resume:description"
                        values={{
                          title: title || data.title || 'Untitled Resume'
                        }}>
                        <span className="font-medium text-gray-900" />
                      </Trans>
                    ),
                    actions: [
                      {
                        label: 'Cancel',
                        type: 'secondary',
                        size: 'sm'
                      },
                      {
                        label: 'Delete',
                        type: 'destructive',
                        size: 'sm',
                        onClick: () =>
                          onSubmitDeleteProfile(data.id, data.title)
                      }
                    ]
                  })
                }
              }
            ]}
            trigger={(openDropdown) => (
              <TextButton
                className="ml-4"
                iconMenus="MoreHorizontal"
                label="More"
                underline={false}
              />
            )}></DropdownMenu>
        </div>
      </div>
      <ShareCVModal
        open={openShareCVModal}
        setOpen={setOpenShareCVModal}
        url={configuration.path.shareCV(
          data.shareLink.replace(`${PUBLIC_APP_URL}/share/cv/`, '')
        )}
      />
    </div>
  )
}

export default ResumeCard
